import { Reducer, AnyAction } from 'redux';
import { AdministrationMessagesFormState, AdministrationMessageItem, AdministrationMessageItemTranslation } from './types-store';
import {
  ACTION, ReceiveLanguageOptions,
  RequestMessage, ReceiveMessage, FailMessage,
  RequestDeleteMessage, ReceiveDeleteMessage, FailDeleteMessage,
  RequestSaveMessage, ReceiveSaveMessage, FailSaveMessage,
  ClearForm,
  ChangeSubject, ChangeMessage, CopySubjectToAll, CopyMessageToAll, ChangeActive,
  UpdateValidation
} from './action-types';
import { clearValidationForAllParameter, clearValidationForSingleParameter } from './reducers.helper';

export const INITIAL_STATE: AdministrationMessagesFormState = {
  message: undefined,
  originalMessage: undefined,
  messageId: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {}
};

const reducers: { [indexer: string]: Reducer<AdministrationMessagesFormState> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: AdministrationMessagesFormState,
    action: ReceiveLanguageOptions): AdministrationMessagesFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_MESSAGE]: (state: AdministrationMessagesFormState,
    action: RequestMessage): AdministrationMessagesFormState => {

    return {
      ...state,
      message: undefined,
      messageId: action.messageId,
      originalMessage: undefined,
      fetching: true
    };
  },

  [ACTION.RECEIVE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: ReceiveMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        message: action.message,
        originalMessage: action.message,
        fetching: false
      }
      : state;
  },

  [ACTION.FAIL_MESSAGE]: (state: AdministrationMessagesFormState,
    action: FailMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        fetching: false
      }
      : state;
  },

  [ACTION.REQUEST_DELETE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: RequestDeleteMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        deleting: true
      }
      : state;
  },

  [ACTION.RECEIVE_DELETE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: ReceiveDeleteMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.FAIL_DELETE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: FailDeleteMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.REQUEST_SAVE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: RequestSaveMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        saving: true
      }
      : state;
  },

  [ACTION.RECEIVE_SAVE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: ReceiveSaveMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        originalMessage: state.message,
        saving: false
      }
      : state;
  },

  [ACTION.FAIL_SAVE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: FailSaveMessage): AdministrationMessagesFormState => {

    return state.messageId === action.messageId
      ? {
        ...state,
        saving: false
      }
      : state;
  },

  [ACTION.CLEAR_FORM]: (state: AdministrationMessagesFormState,
    action: ClearForm): AdministrationMessagesFormState => {

    return {
      ...state,
      message: undefined,
      messageId: undefined,
      originalMessage: undefined,
      validation: {},
      fetching: false,
      saving: false,
      deleting: false
    };
  },

  [ACTION.CHANGE_SUBJECT]: (state: AdministrationMessagesFormState,
    action: ChangeSubject): AdministrationMessagesFormState => {

    const message = state.message || {} as AdministrationMessageItem;
    const translations = message.translations || [];

    return {
      ...state,
      message: {
        ...message,
        translations: translations.some(language => language.language === action.languageId)
          ? translations.map(
            language => language.language === action.languageId
              ? {
                ...language,
                subject: action.subject
              }
              : language
          )
          : [
            ...translations,
            {
              language: action.languageId,
              subject: action.subject
            } as AdministrationMessageItemTranslation
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'subject', action.languageId)
    };
  },

  [ACTION.CHANGE_MESSAGE]: (state: AdministrationMessagesFormState,
    action: ChangeMessage): AdministrationMessagesFormState => {

    const message = state.message || {} as AdministrationMessageItem;
    const translations = message.translations || [];

    return {
      ...state,
      message: {
        ...message,
        translations: translations.some(language => language.language === action.languageId)
          ? translations.map(
            language => language.language === action.languageId
              ? {
                ...language,
                message: action.message
              }
              : language
          )
          : [
            ...translations,
            {
              language: action.languageId,
              message: action.message
            } as AdministrationMessageItemTranslation
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'message', action.languageId)
    };
  },

  [ACTION.COPY_SUBJECT_TO_ALL]: (state: AdministrationMessagesFormState,
    action: CopySubjectToAll): AdministrationMessagesFormState => {

    const message = state.message || {} as AdministrationMessageItem;
    const translations = message.translations || [];
    const targetTranslation = translations.find(language => language.language === action.languageId);

    if (targetTranslation === undefined) {
      return state;
    }

    const updatedTranslations = translations.map(
      language => ({
        ...language,
        subject: targetTranslation.subject
      })
    );
    const newTranslations = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !translations.some(language => language.language === languageId))
      .map(languageId => ({
        language: languageId,
        subject: targetTranslation.subject
      } as AdministrationMessageItemTranslation));

    return {
      ...state,
      message: {
        ...message,
        translations: [
          ...updatedTranslations,
          ...newTranslations
        ]
      },
      validation: clearValidationForAllParameter(state.validation, 'subject')
    };
  },

  [ACTION.COPY_MESSAGE_TO_ALL]: (state: AdministrationMessagesFormState,
    action: CopyMessageToAll): AdministrationMessagesFormState => {

    const message = state.message || {} as AdministrationMessageItem;
    const translations = message.translations || [];
    const targetTranslation = translations.find(language => language.language === action.languageId);

    if (targetTranslation === undefined) {
      return state;
    }

    const updatedTranslations = translations.map(
      language => ({
        ...language,
        message: targetTranslation.message
      })
    );
    const newTranslations = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !translations.some(language => language.language === languageId))
      .map(languageId => ({
        language: languageId,
        message: targetTranslation.message
      } as AdministrationMessageItemTranslation));

    return {
      ...state,
      message: {
        ...message,
        translations: [
          ...updatedTranslations,
          ...newTranslations
        ]
      },
      validation: clearValidationForAllParameter(state.validation, 'message')
    };
  },

  [ACTION.CHANGE_ACTIVE]: (state: AdministrationMessagesFormState,
    action: ChangeActive): AdministrationMessagesFormState => {

    const message = state.message || {} as AdministrationMessageItem;

    return {
      ...state,
      message: {
        ...message,
        active: action.active
      }
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: AdministrationMessagesFormState,
    action: UpdateValidation): AdministrationMessagesFormState => {

    return {
      ...state,
      validation: action.validation
    };
  }
};

export const reducer: Reducer<AdministrationMessagesFormState> = (
  state: AdministrationMessagesFormState = INITIAL_STATE, action: AnyAction) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
